import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { listEvalExperimentsOptions, listEvalFixturesOptions } from '@/api/generated/@tanstack/react-query.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { pct, scoreTone, StatusPill } from '@/components/Evals/format';
import { NewExperimentDialog } from '@/components/Evals/NewExperimentDialog';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/experiments/')({
  component: ExperimentsPage,
});

function ExperimentsPage() {
  const experiments = useQuery({ ...listEvalExperimentsOptions(), refetchInterval: 10000 });
  const fixtures = useQuery(listEvalFixturesOptions());
  const fixtureName = (id: string) => fixtures.data?.find((f) => f.id === id)?.name ?? id;

  return (
    <EvalShell
      title="Experiments"
      subtitle="Batches of live runs, each compared with the baseline."
      actions={<NewExperimentDialog />}
    >
      <Panel>
        {!experiments.data ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : !experiments.data.length ? (
          <p className="text-sm text-neutral-400">No experiments yet.</p>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3 font-medium">Experiment</th>
                <th className="py-1.5 pr-3 font-medium">Sessions</th>
                <th className="py-1.5 pr-3 font-medium">Runs</th>
                <th className="py-1.5 pr-3 font-medium">Score</th>
                <th className="py-1.5 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {experiments.data.map((exp) => (
                <tr key={exp.id} className="border-t border-neutral-100 align-top">
                  <td className="py-2 pr-3">
                    <Link
                      to="/evals/experiments/$experimentId"
                      params={{ experimentId: exp.id }}
                      className="font-medium text-neutral-900 hover:underline"
                    >
                      {exp.name}
                    </Link>
                    {(exp.baseline_for ?? []).length > 0 && (
                      <span className="ml-2 rounded-full bg-[#AAE73D] px-2 py-0.5 text-[10px] font-semibold uppercase text-neutral-900">
                        baseline
                      </span>
                    )}
                    {exp.notes && <p className="max-w-md truncate text-[12px] text-neutral-500">{exp.notes}</p>}
                  </td>
                  <td className="py-2 pr-3 text-neutral-600">{exp.fixture_ids.map(fixtureName).join(', ')}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(exp.run_counts ?? {}).map(([status, n]) => (
                        <span key={status} className="flex items-center gap-1">
                          <StatusPill status={status} />
                          <span className="text-[11px] text-neutral-500">{n}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-[12px] font-semibold', scoreTone(exp.score))}>
                      {pct(exp.score)}
                    </span>
                  </td>
                  <td className="py-2 text-[12px] text-neutral-500">
                    {exp.created_at ? new Date(exp.created_at).toLocaleString() : ''}
                    {exp.git_sha && <span className="ml-1 font-mono text-neutral-400">{exp.git_sha}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </EvalShell>
  );
}
